import type { AfterViewInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  linkedSignal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { EllipsisDirective } from '../../directives/ellipsis/ellipsis';
import { ControlsVisibility } from '../../services/controls-visibility/controls-visibility';
import { FeatureFlagsService } from '../../services/feature-flags/feature-flags';
import { FullscreenService } from '../../services/fullscreen/fullscreen';
import { MetadataService } from '../../services/metadata/metadata';
import { NavigateService } from '../../services/navigate/navigate';
import { BookmarksService } from '../../services/selections/bookmarks';
import { TranslationService } from '../../services/translation/translation';
import { NavigationTabsComponent } from '../navigation/navigation-tabs/navigation-tabs';
import { QuestionsComponent } from '../questions/questions/questions';
import { ReaderSettingsTabsComponent } from '../reader-settings/reader-settings-tabs/reader-settings-tabs';
import { SkeletonComponent } from '../skeleton/skeleton';

@Component({
  selector: 'nr-header',
  imports: [
    EllipsisDirective,
    TranslatePipe,
    NavigationTabsComponent,
    ReaderSettingsTabsComponent,
    SkeletonComponent,
    QuestionsComponent,
  ],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HeaderComponent implements AfterViewInit {
  private readonly metadataService = inject(MetadataService);
  private readonly controlsVisibility = inject(ControlsVisibility);
  private readonly bookmarksService = inject(BookmarksService);
  private readonly translationService = inject(TranslationService);
  private readonly navigateService = inject(NavigateService);

  private updatingBookmark = false;

  readonly featureFlags = inject(FeatureFlagsService);
  readonly fullscreenService = inject(FullscreenService);
  readonly currentBookmark = this.bookmarksService.currentBookmark;
  readonly activeBookmark = linkedSignal(() => Boolean(this.currentBookmark()));

  readonly resourceTitle = computed(() => {
    const localizedTitle = this.metadataService.resourceTitle();
    const lang = this.translationService.lang();
    return localizedTitle?.getTranslation(lang) ?? '';
  });
  readonly isShowingActions = this.controlsVisibility.isHeaderFooterVisible;
  readonly headerActionsClass = computed((): { fadeOut: boolean; elementHidden: boolean } => {
    const fadeOut = !this.isShowingActions();
    const elementHidden = true;
    return { fadeOut, elementHidden };
  });
  readonly bookmarkClass = computed((): { fadeOut: boolean; elementHidden: boolean } => {
    const disabled = !this.activeBookmark();
    const fadeOut = !this.isShowingActions() && disabled;
    const elementHidden = true;
    return { fadeOut, elementHidden };
  });
  readonly isPreviewMode = linkedSignal(() => this.metadataService.isPreviewMode());

  ngAfterViewInit(): void {
    this.controlsVisibility.addElementsToGroup('header-footer', [
      document.querySelector('header.header')!,
    ]);
  }

  async toggleBookmark(): Promise<void> {
    if (this.updatingBookmark) return;
    try {
      this.updatingBookmark = true;
      this.activeBookmark.update((value) => !value);
      const bookmark = this.currentBookmark();
      await (bookmark
        ? this.bookmarksService.removeBookmark(bookmark.id)
        : this.bookmarksService.saveBookmark());
    } catch (error) {
      //TODO: Toast error
      console.error('Error saving bookmark:', error);
      this.activeBookmark.update((value) => !value);
    } finally {
      this.updatingBookmark = false;
    }
  }

  goToPlatform(): void {
    this.navigateService.goToPlatform();
  }
}
